import { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { useCached } from "../lib/dataCache";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Package, Users, Wallet, Activity, AlertTriangle, ArrowUpRight, TrendingUp, Trophy, FileDown, Boxes, PackageX } from "lucide-react";
import { Link } from "react-router-dom";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const fmtIDR = (n) => "Rp" + Number(n || 0).toLocaleString("id-ID");
const fmtShort = (n) => n >= 1000000 ? (n/1000000).toFixed(1) + "jt" : n >= 1000 ? (n/1000).toFixed(0) + "rb" : n.toString();

function Stat({ icon: Icon, label, value, tid, sub }) {
  return (
    <Card className="border-neutral-200 shadow-none" data-testid={tid}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.1em] text-neutral-500 font-semibold">{label}</div>
            <div className="font-display font-bold text-3xl text-neutral-900 mt-2 font-mono-tab">{value}</div>
            {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
          </div>
          <div className="w-9 h-9 rounded flex items-center justify-center bg-neutral-100">
            <Icon className="w-5 h-5 text-neutral-700" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const TIER = {
  tinggi: { t: "Tinggi", c: "#065F46", bg: "#D1FAE5" },
  sedang: { t: "Sedang", c: "#92400E", bg: "#FEF3C7" },
  rendah: { t: "Rendah", c: "#525252", bg: "#F5F5F5" },
};
function TierBadge({ tier }) {
  const s = TIER[tier] || TIER.rendah;
  return <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded" style={{ color: s.c, background: s.bg }}>{s.t}</span>;
}

export default function Dashboard() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [stats, setStats] = useCached("dash_stats", null);
  const [analytics, setAnalytics] = useCached("dash_analytics", null);
  const now = new Date();
  const [pdfMonth, setPdfMonth] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}`);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/dashboard/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);

  const downloadPdf = async () => {
    const [y, m] = pdfMonth.split("-").map(Number);
    if (!y || !m) return toast.error("Pilih bulan dulu");
    setDownloading(true);
    try {
      const token = localStorage.getItem("rp_token");
      const res = await fetch(`${API}/reports/monthly-pdf?year=${y}&month=${m}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Gagal generate PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-${y}-${String(m).padStart(2,"0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF diunduh");
    } catch (err) { toast.error(err.message || "Gagal unduh"); }
    setDownloading(false);
  };

  if (!stats) return <div className="text-neutral-500">Memuat...</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Dasbor</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-neutral-900 mt-1">Ringkasan Operasional</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isOwner && (
            <>
              <input
                data-testid="pdf-month"
                type="month"
                value={pdfMonth}
                onChange={e => setPdfMonth(e.target.value)}
                className="h-9 px-3 rounded-md border border-neutral-200 bg-white text-sm font-mono-tab"
              />
              <Button data-testid="export-pdf-btn" variant="outline" onClick={downloadPdf} disabled={downloading}>
                <FileDown className="w-4 h-4 mr-1" /> {downloading ? "Menyiapkan..." : "Ekspor PDF"}
              </Button>
            </>
          )}
          <Link to="/pos"><Button data-testid="quick-new-rental" className="text-white" style={{ background: "var(--brand-primary)" }}>
            Transaksi Baru <ArrowUpRight className="w-4 h-4 ml-1" />
          </Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
        <Stat icon={Wallet} label="Omzet Bulan Ini" value={fmtIDR(stats.revenue_month)} tid="stat-revenue" sub={`${stats.total_rentals} transaksi total`} />
        <Stat icon={Activity} label="Sewa Aktif" value={stats.active_rentals} tid="stat-active" sub="Reserved + Disewa" />
        <Stat icon={Package} label="Total Alat" value={stats.total_products} tid="stat-products" />
        <Stat icon={Users} label="Pelanggan" value={stats.total_customers} tid="stat-customers" />
      </div>

      {stats.low_stock?.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded" data-testid="low-stock-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold text-red-900">Peringatan Stok Kritis</div>
              <div className="text-sm text-red-800 mt-1">
                {stats.low_stock.map((p) => `${p.name} (${p.available}/${p.total})`).join(" · ")}
              </div>
            </div>
          </div>
        </div>
      )}

      {analytics && (
        <Card className="border-neutral-200 shadow-none">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4" />
              <h3 className="font-display font-semibold text-lg">Grafik Tren Penjualan</h3>
            </div>
            <Tabs defaultValue="daily">
              <TabsList>
                <TabsTrigger value="daily" data-testid="chart-daily">7 Hari</TabsTrigger>
                <TabsTrigger value="weekly" data-testid="chart-weekly">4 Minggu</TabsTrigger>
                <TabsTrigger value="monthly" data-testid="chart-monthly">6 Bulan</TabsTrigger>
              </TabsList>
              {["daily","weekly","monthly"].map(k => (
                <TabsContent key={k} value={k}>
                  <div className="h-72 mt-4" data-testid={`chart-${k}-body`}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={analytics[k]}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="label" style={{fontSize: 11}} />
                        <YAxis tickFormatter={fmtShort} style={{fontSize: 11}} />
                        <Tooltip formatter={(v, name) => name === "revenue" ? fmtIDR(v) : v} />
                        <Line type="monotone" dataKey="revenue" stroke="var(--brand-primary)" strokeWidth={2.5} dot={{r: 4, fill: "var(--brand-primary)"}} name="Omzet" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {analytics?.top_products?.length > 0 && (
          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-4 h-4 text-red-600" />
                <h3 className="font-display font-semibold text-lg">Produk Terlaris</h3>
              </div>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.top_products} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis type="number" tickFormatter={fmtShort} style={{fontSize: 11}} />
                    <YAxis type="category" dataKey="name" width={100} style={{fontSize: 11}} />
                    <Tooltip formatter={(v) => fmtIDR(v)} />
                    <Bar dataKey="revenue" fill="var(--brand-primary)" name="Omzet" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-neutral-200 shadow-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-lg">Transaksi Terkini</h3>
              <Link to="/bookings" className="text-xs uppercase tracking-widest font-semibold text-red-600">Lihat Semua</Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200">
                    <th className="py-3">Kode</th><th>Pelanggan</th><th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_rentals.map(r => (
                    <tr key={r.id} className="border-b border-neutral-100">
                      <td className="py-3 font-mono-tab text-xs">{r.code}</td>
                      <td>{r.customer_name}</td>
                      <td className="text-right font-mono-tab font-semibold">{fmtIDR(r.total)}</td>
                    </tr>
                  ))}
                  {stats.recent_rentals.length === 0 && <tr><td colSpan={3} className="py-8 text-center text-neutral-500">Belum ada transaksi</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {analytics?.product_performance?.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="analysis-section">
          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Boxes className="w-4 h-4 text-neutral-700" />
                <h3 className="font-display font-semibold text-lg">Performa Barang</h3>
              </div>
              <p className="text-xs text-neutral-500 mb-3">Peringkat barang paling laku (Tinggi → Sedang → Rendah) berdasarkan omzet.</p>
              <div className="max-h-80 overflow-y-auto">
                <table className="w-full text-sm" data-testid="product-performance-table">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-neutral-500 border-b border-neutral-200 bg-neutral-50">
                      <th className="py-2 px-2">Barang</th><th className="px-2">Performa</th><th className="px-2 text-right">Qty×Hari</th><th className="px-2 text-right">Omzet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.product_performance.map((p, i) => (
                      <tr key={i} className="border-b border-neutral-100" data-testid={`perf-row-${i}`}>
                        <td className="py-2 px-2 font-medium">{p.name}</td>
                        <td className="px-2"><TierBadge tier={p.tier} /></td>
                        <td className="px-2 text-right font-mono-tab">{p.count}</td>
                        <td className="px-2 text-right font-mono-tab font-semibold">{fmtIDR(p.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          <Card className="border-neutral-200 shadow-none">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <PackageX className="w-4 h-4 text-red-600" />
                <h3 className="font-display font-semibold text-lg">Sering Kehabisan Stok</h3>
              </div>
              <p className="text-xs text-neutral-500 mb-3">Jumlah hari stok habis dalam 90 hari terakhir — pertimbangkan menambah unit.</p>
              {analytics.stockout_products?.length > 0 ? (
                <div className="max-h-72 overflow-y-auto space-y-2" data-testid="stockout-list">
                  {analytics.stockout_products.map((p, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded border border-neutral-100" data-testid={`stockout-row-${i}`}>
                      <div className="font-medium text-sm">{p.name}<span className="text-xs text-neutral-500 ml-2">stok {p.total}</span></div>
                      <span className="text-xs font-bold px-2 py-1 rounded bg-red-50 text-red-700">{p.stockout_days} hari habis</span>
                    </div>
                  ))}
                </div>
              ) : <div className="text-sm text-neutral-500 py-8 text-center">Tidak ada barang yang kehabisan stok dalam 90 hari terakhir.</div>}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
